import { z } from "zod";
import { assistantIds } from "../generated/storyblok/assistants.js";
import { ChatThreadConfigurationSchema } from "./ChatThread.js";
import { ToneDefinitionIdSchema } from "./ToneDefinition.js";

export const AssistantSchema = z.object({
  id: z.enum(assistantIds),
  name: z.string(),
  description: z.string(),
  prompt: z.string(),
  iconName: z.string().optional(),
  priority: z.number().optional(),
});

export type Assistant = z.infer<typeof AssistantSchema>;

export const AssistantConversationInputSchema = z.object({
  convoId: z.uuid(),
  assistantPersona: AssistantSchema.shape.id,
  input: z.string(),
  context: z
    .object({
      text: z.string(),
      role: z.enum(["refine"]),
    })
    .optional(),
});

const stringOrObject = z.xor([z.string(), z.record(z.string(), z.unknown())]);

export const AssistantConversationInputContextV2Schema = z.discriminatedUnion(
  "type",
  [
    z.object({
      type: z.literal("knowledge"),
      blockId: z.uuid(),
    }),
    z.object({
      type: z.literal("asset"),
      assetId: z.uuid(),
      content: stringOrObject,
    }),
    z.object({
      type: z.literal("directMessage"),
    }),
    z.object({
      type: z.literal("networkContact"),
      contactId: z.uuid(),
      content: stringOrObject,
    }),
    z.object({
      type: z.literal("blockStack"),
      stack: z.string(),
    }),
    z.object({
      type: z.literal("insight"),
      content: stringOrObject,
    }),
  ],
);

export const AssistantConversationInputV2Schema = z.object({
  chatThreadId: z.uuid(),
  toneDefinitionId: ToneDefinitionIdSchema.optional(),
  messages: z.array(z.record(z.string(), z.any())),
  context: AssistantConversationInputContextV2Schema.optional(),
  source: z.string().max(128).optional(),
  externalSource: ChatThreadConfigurationSchema.shape.externalSource,
  initialChatThreadTitle: z.string().optional(),
});

export type AssistantConversationInput = z.infer<
  typeof AssistantConversationInputSchema
>;

export type AssistantConversationInputV2 = z.infer<
  typeof AssistantConversationInputV2Schema
>;

export type AssistantConversationInputContextV2 = z.infer<
  typeof AssistantConversationInputContextV2Schema
>;
